// Templates.jsx — list origination templates with section details
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Chip, Button, Accordion, AccordionSummary, AccordionDetails, CircularProgress,
} from "@mui/material";
import { ChevronDown, PlayCircle } from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import { originationApi } from "../api/api";

const STAGE_COLOR = {
  onboarding: "#2E5A35",
  credit_appraisal: "#1E2A38",
  renewal: "#8F5E14",
  investment_dd: "#5D4037",
};

export default function Templates() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    originationApi.listTemplates().then(({ data }) => setList(data.templates || [])).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout title="Origination Templates" subtitle="REUSABLE ASSESSMENT WORKFLOWS">
      {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box> : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.6 }}>
          {list.map((t) => (
            <Paper key={t.template_id} sx={{ p: 0, overflow: "hidden", borderLeft: `3px solid ${STAGE_COLOR[t.stage] || "#1E2A38"}` }}
              data-testid={`template-${t.template_id}`}>
              <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", gap: 0.6, mb: 0.6 }}>
                    <Chip size="small" label={t.stage} sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10 }} />
                    <Chip size="small" label={t.portfolio} sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10 }} />
                    <Chip size="small" label={t.borrower_type}
                      sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, background: "#F0EFEA" }} />
                  </Box>
                  <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>{t.name}</Box>
                  <Box sx={{ fontSize: 12.5, color: "#4B5563", maxWidth: 800, mt: 0.4 }}>{t.description}</Box>
                </Box>
                <Button variant="contained" size="small" startIcon={<PlayCircle size={13} />}
                  onClick={() => navigate("/origination")}
                  data-testid={`launch-${t.template_id}`}
                  sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
                  Use in console
                </Button>
              </Box>
              {/* Sections */}
              <Box sx={{ borderTop: "1px solid #F0EFEA" }}>
                {(t.sections || []).map((s) => (
                  <Accordion key={s.id} disableGutters elevation={0} sx={{ borderTop: "1px solid #F0EFEA", "&::before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ChevronDown size={15} />}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                        <Box sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{s.title}</Box>
                        <Chip size="small" label={`${s.fields?.length || 0} fields`} sx={{ fontSize: 9.5, fontFamily: "IBM Plex Mono, monospace" }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 1 }}>
                        {(s.fields || []).map((f) => (
                          <Box key={f.key} sx={{ p: 1, border: "1px solid #F0EFEA", borderRadius: 1 }}>
                            <Box className="cb-mono" sx={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                              {f.type}{f.required ? " · required" : ""}
                            </Box>
                            <Box sx={{ fontSize: 12.5, fontWeight: 500, color: "#111827", mt: 0.3 }}>{f.label}</Box>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </DashboardLayout>
  );
}
